import { Button, DisabledWithReason, Spinner, cn } from '@dawn/ui';
import { Pencil, RotateCw, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { useResendInvitation } from '@/hooks/mutations/use-resend-invitation';
import { useRevokeInvitation } from '@/hooks/mutations/use-revoke-invitation';
import { usePermissions } from '@/hooks/use-permissions';
import { getActionErrorMessage } from '@/lib/admin-action-error';
import type { InvitationListItem } from '@/lib/api';
import { formatTimeRemaining, getInvitationStatus, isExpiringSoon } from '@/lib/invitation-status';

import { MANAGE_REASON } from './AdminsTable';
import { ConfirmDialog } from './ConfirmDialog';
import { RoleChips } from './RoleChips';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

interface InvitationsTableProps {
  invitations: InvitationListItem[];
  /** At the admin cap — reviving an expired invite would breach it, so resend is blocked there. */
  atCap: boolean;
  /** Edit an invitation's roles; the section owns that dialog so it outlives the table. */
  onEdit: (invitation: InvitationListItem) => void;
}

/**
 * Invitations table. Status (Pending / Expired) and the expiry countdown are
 * derived from each invite's `expiresAt`; invites within 24h of expiry are
 * highlighted in red. Edit changes the roles the invitee will get (pending or
 * expired); Resend revives an invite to a fresh window (blocked on expired rows
 * at the cap); Revoke removes it behind a destructive confirm.
 */
export function InvitationsTable({ invitations, atCap, onEdit }: InvitationsTableProps) {
  const now = Date.now();
  const { can } = usePermissions();
  const manageReason = can('users.manage') ? null : MANAGE_REASON;
  const [revokeTarget, setRevokeTarget] = useState<InvitationListItem | null>(null);
  const resend = useResendInvitation();
  const revoke = useRevokeInvitation();
  const resending = resend.isPending;
  const revoking = revoke.isPending;

  const handleConfirmRevoke = async () => {
    if (!revokeTarget) return;
    try {
      await revoke.mutateAsync(revokeTarget.id);
      setRevokeTarget(null);
    } catch {
      // Error surfaces inside the dialog; keep it open so the user can retry.
    }
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!open) {
      setRevokeTarget(null);
      revoke.reset();
    }
  };

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b">
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                Email
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                Status
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                Roles
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                Date invited
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                Expires
              </th>
              <th className="px-4 py-3 text-right text-sm font-medium text-muted-foreground">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {invitations.map((inv) => {
              const status = getInvitationStatus(inv.expiresAt, now);
              const expired = status === 'expired';
              const soon = isExpiringSoon(inv.expiresAt, now);
              const resendingRow = resending && resend.variables === inv.id;
              const resendFailedRow = resend.isError && resend.variables === inv.id;
              // Reviving an expired invite adds a pending slot — block it at the cap.
              const resendBlockedByCap = expired && atCap;

              return (
                <tr
                  key={inv.id}
                  className="border-b last:border-0"
                  data-testid={`invitation-row-${inv.id}`}
                >
                  <td className="px-4 py-3 text-sm text-dawn-black">{inv.email}</td>
                  <td className="px-4 py-3 text-sm">
                    <span
                      className={cn(
                        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                        expired ? 'bg-error/10 text-error' : 'bg-orange-100 text-dawn-orange'
                      )}
                      data-testid={`invitation-status-${inv.id}`}
                    >
                      {expired ? 'Expired' : 'Pending'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <RoleChips roles={inv.roles} testId={`invitation-roles-${inv.id}`} />
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {formatDate(inv.createdAt)}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span
                      className={cn(
                        expired || soon ? 'font-medium text-error' : 'text-muted-foreground'
                      )}
                      data-testid={`invitation-countdown-${inv.id}`}
                    >
                      {formatTimeRemaining(inv.expiresAt, now)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {resendFailedRow && (
                        <span
                          className="max-w-[16rem] text-right text-xs text-error"
                          data-testid={`invitation-resend-error-${inv.id}`}
                        >
                          {getActionErrorMessage(
                            resend.error,
                            "Couldn't resend. Please try again."
                          )}
                        </span>
                      )}
                      <DisabledWithReason reason={manageReason}>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="gap-1.5"
                          aria-label={`Edit roles of the invitation to ${inv.email}`}
                          onClick={() => onEdit(inv)}
                          disabled={resending || revoking}
                          data-testid={`invitation-edit-button-${inv.id}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          Edit
                        </Button>
                      </DisabledWithReason>
                      <DisabledWithReason reason={manageReason}>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="gap-1.5"
                          aria-label={`Resend invitation to ${inv.email}`}
                          onClick={() => resend.mutate(inv.id)}
                          disabled={resending || revoking || resendBlockedByCap}
                          title={
                            resendBlockedByCap
                              ? 'At the user limit — remove a user or revoke a pending invite first'
                              : undefined
                          }
                          data-testid={`invitation-resend-button-${inv.id}`}
                        >
                          {resendingRow ? (
                            <Spinner animate size={14} />
                          ) : (
                            <RotateCw className="h-3.5 w-3.5" />
                          )}
                          Resend
                        </Button>
                      </DisabledWithReason>
                      <DisabledWithReason reason={manageReason} placement="top-end">
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Revoke invitation to ${inv.email}`}
                          onClick={() => setRevokeTarget(inv)}
                          disabled={revoking || resending}
                          data-testid={`invitation-revoke-button-${inv.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-error" />
                        </Button>
                      </DisabledWithReason>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        open={revokeTarget !== null}
        onOpenChange={handleDialogOpenChange}
        title="Revoke invitation"
        description={
          <>
            Revoke the invitation to{' '}
            <span className="font-medium text-dawn-black">{revokeTarget?.email}</span>? The link in
            their email will stop working.
          </>
        }
        confirmLabel="Revoke"
        onConfirm={() => void handleConfirmRevoke()}
        loading={revoking}
        error={
          revoke.isError
            ? getActionErrorMessage(
                revoke.error,
                "Couldn't revoke the invitation. Please try again."
              )
            : null
        }
        data-testid="revoke-invitation-dialog"
      />
    </>
  );
}
